import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

const MEDALS = ['🥇', '🥈', '🥉', '4.', '5.', '5.', '7.', '7.'];
const fmtDate = d => d ? new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' }) : '';

export default function OeffentlicheErgebnisse() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    async function load() {
      try {
        const r = await fetch(`/api/turniere/public/${id}/ergebnisse`);
        const d = await r.json();
        if (d.success) setData(d);
        else setError(d.error || 'Keine Ergebnisse gefunden');
      } catch { setError('Verbindungsfehler'); }
      finally { setLoading(false); }
    }
    load();
    const interval = setInterval(load, 60000); // auto-refresh every 60s
    return () => clearInterval(interval);
  }, [id]);

  const filtered = data?.divisionen?.filter(div =>
    !search || div.code.toLowerCase().includes(search.toLowerCase()) ||
    div.athleten.some(a => `${a.vorname} ${a.nachname} ${a.verein_name}`.toLowerCase().includes(search.toLowerCase()))
  ) || [];

  return (
    <div style={{ minHeight: '100vh', background: 'radial-gradient(ellipse at top, #1a0a0a 0%, #0a0a14 100%)', fontFamily: 'Inter, -apple-system, sans-serif', color: '#fff' }}>
      {/* Nav */}
      <nav style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(40px)', borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '1rem 2rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <img src="/logo.webp" alt="TDA" style={{ height: 32, borderRadius: 8 }} />
        <span style={{ fontWeight: 700, fontSize: '1rem' }}>TDA International</span>
        <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)' }}>Live-Ergebnisse</span>
      </nav>

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '2rem 1.5rem' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '4rem', color: 'rgba(255,255,255,0.4)' }}>
            <div style={{ width: 40, height: 40, border: '3px solid rgba(255,255,255,0.1)', borderTopColor: '#c53030', borderRadius: '50%', animation: 'spin 0.9s linear infinite', margin: '0 auto 1rem' }} />
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
            Lade Ergebnisse…
          </div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: '4rem', color: '#f87171' }}>{error}</div>
        ) : (
          <>
            {/* Tournament Header */}
            <div style={{ marginBottom: '2rem' }}>
              <h1 style={{ fontSize: '2rem', fontWeight: 800, margin: '0 0 0.25rem', letterSpacing: '-1px' }}>{data.turnier?.name}</h1>
              <div style={{ display: 'flex', gap: '1.5rem', color: 'rgba(255,255,255,0.5)', fontSize: '0.875rem', flexWrap: 'wrap' }}>
                {data.turnier?.datum && <span>📅 {fmtDate(data.turnier.datum)}</span>}
                {data.turnier?.ort && <span>📍 {data.turnier.ort}</span>}
                <span>{filtered.length} Disziplinen</span>
              </div>
            </div>

            {/* Search */}
            {data.divisionen?.length > 0 && (
              <input
                value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Disziplin oder Athlet suchen…"
                style={{ width: '100%', boxSizing: 'border-box', padding: '0.7rem 1rem', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, color: '#fff', fontSize: '0.9rem', marginBottom: '1.5rem', fontFamily: 'inherit', outline: 'none' }}
              />
            )}

            {filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'rgba(255,255,255,0.3)' }}>
                {data.divisionen?.length === 0 ? 'Noch keine Ergebnisse verfügbar.' : 'Keine Treffer.'}
              </div>
            ) : (
              filtered.map(div => (
                <div key={div.code} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 14, overflow: 'hidden', marginBottom: '1rem' }}>
                  <div style={{ background: 'rgba(197,48,48,0.15)', borderBottom: '1px solid rgba(197,48,48,0.2)', padding: '0.75rem 1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#fca5a5' }}>{div.code}</span>
                    <span style={{ fontSize: '0.72rem', background: 'rgba(197,48,48,0.25)', color: '#fca5a5', padding: '0.2rem 0.6rem', borderRadius: 10, border: '1px solid rgba(197,48,48,0.35)' }}>{div.athleten.length} Platzierte</span>
                  </div>
                  {div.athleten.map((a, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.65rem 1.25rem', borderBottom: '1px solid rgba(255,255,255,0.04)', background: i === 0 ? 'rgba(215,162,58,0.04)' : 'transparent' }}>
                      <span style={{ fontSize: '1.1rem', minWidth: 28, textAlign: 'center' }}>{MEDALS[a.platzierung - 1] || a.platzierung + '.'}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.9rem', color: i === 0 ? '#f7d98f' : '#fff' }}>{a.nachname}, {a.vorname}</div>
                        <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)' }}>{a.verein_name}{a.verein_ort ? ` · ${a.verein_ort}` : ''}</div>
                      </div>
                      {a.gurtfarbe && <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)' }}>{a.gurtfarbe}</span>}
                    </div>
                  ))}
                </div>
              ))
            )}

            <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.2)', fontSize: '0.75rem', marginTop: '2rem' }}>
              Automatische Aktualisierung alle 60 Sekunden · TDA International
            </p>
          </>
        )}
      </div>
    </div>
  );
}
